import { inject, Injectable } from "@angular/core";
import { combineLatest, map, Observable, of } from "rxjs";
import { AccountSelectionService, AuthService, OrganizationAccountGroupService } from "@front/m19-services";
import { AccountState, AccountSubType, AccountType, Plan } from "@front/m19-api-client";
import { Marketplaces } from "@front/m19-models";
import { BoardType, LayoutSelectorService } from "@m19-board/layout-selector.service";
import { keywordRankingAvailableFor } from "@m19-board/tracking/KeywordRankingAvailability";
import { OverlayCategory, OverlayComponent, OverlayRule, OverlayRuleConfig } from "./overlay-rules";
import { OnboardingService } from "@m19-board/services/onboarding.service";

@Injectable({
  providedIn: "root",
})
export class OverlayService {
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly organizationService = inject(OrganizationAccountGroupService);
  private readonly layoutSelectorService = inject(LayoutSelectorService);
  private readonly onboardingService = inject(OnboardingService);
  private readonly authService = inject(AuthService);

  private readonly selectedAccountMarketplace$ = this.accountSelectionService.singleAccountMarketplaceSelection$;
  private readonly allOrgs$ = this.organizationService.allOrganizationAccountGroups$;
  private readonly selectedAccountGroup$ = this.accountSelectionService.accountGroupSelection$;
  private readonly boardType = this.layoutSelectorService.getBoardType();
  private readonly selectedOrg$ = combineLatest([this.allOrgs$, this.selectedAccountGroup$]).pipe(
    map(([allOrgs, selectedAccountGroup]) => allOrgs.find((x) => x.id == selectedAccountGroup.organizationId)),
  );
  private readonly isBidderOn$ = combineLatest([this.selectedOrg$, this.selectedAccountMarketplace$]).pipe(
    map(([selectedOrg, accountMarketplace]) => {
      return selectedOrg?.getAccountState(accountMarketplace) == AccountState.BIDDER_ON;
    }),
  );

  private static readonly CATEGORY_PRIORITY = [
    OverlayCategory.INCOMPATIBLE,
    OverlayCategory.BROKEN,
    OverlayCategory.INACTIVE,
  ];

  private readonly OVERLAY_RULES: Record<OverlayRule, OverlayRuleConfig> = {
    [OverlayRule.SB_NOT_SUPPORTED_FOR_KDP]: {
      category: OverlayCategory.INCOMPATIBLE,
      applies: () => this.isKdpAccount(),
    },
    [OverlayRule.SD_NOT_SUPPORTED_FOR_KDP]: {
      category: OverlayCategory.INCOMPATIBLE,
      applies: () => this.isKdpAccount(),
    },
    [OverlayRule.MARKETPLACE_NOT_INCLUDED]: {
      category: OverlayCategory.INCOMPATIBLE,
      applies: () => this.marketplaceNotIncluded(),
    },
    [OverlayRule.HOURLY_UNSUPPORTED_MARKETPLACE]: {
      category: OverlayCategory.INCOMPATIBLE,
      applies: () => this.hourlyUnsupportedMarketplace(),
    },
    [OverlayRule.HOURLY_NOT_IN_BILLING_PLAN]: {
      category: OverlayCategory.INCOMPATIBLE,
      applies: () => this.hourlyNotInBillingPlan(),
    },
    [OverlayRule.PROFIT_AND_LOSS_VENDOR_NOT_SUPPORTED]: {
      category: OverlayCategory.INCOMPATIBLE,
      applies: () => this.isVendor(),
    },
    [OverlayRule.KEYWORD_TRACKER_NOT_SUPPORTED]: {
      category: OverlayCategory.INCOMPATIBLE,
      applies: () => this.keywordRankingNotSupported(),
    },
    [OverlayRule.SHARE_OF_VOICE_NOT_SUPPORTED]: {
      category: OverlayCategory.INCOMPATIBLE,
      applies: () => this.keywordRankingNotSupported(),
    },
    [OverlayRule.PRODUCT_TOUR]: {
      category: OverlayCategory.INCOMPATIBLE,
      applies: () => this.authService.isProductTour$,
    },
    [OverlayRule.MANUAL_ACTIVATION_REQUIRED]: {
      category: OverlayCategory.BROKEN,
      applies: () => this.manualActivationRequired(),
    },
    [OverlayRule.ADVERTISING_API_ACCESS_LOST]: {
      category: OverlayCategory.BROKEN,
      applies: () => this.advertisingApiAccessLost(),
    },
    [OverlayRule.INVALID_SELLING_PARTNER_API_ACCESS]: {
      category: OverlayCategory.BROKEN,
      applies: () => this.invalidSellingPartnerApiAccess(),
    },
    [OverlayRule.START_FREE_TEST]: {
      category: OverlayCategory.INACTIVE,
      applies: () => this.shouldStartFreeTest(),
    },
    [OverlayRule.ONBOARDING_CALL_SCHEDULED]: {
      category: OverlayCategory.INACTIVE,
      applies: () => this.onboardingCallScheduled(),
    },
    [OverlayRule.SCHEDULE_ONBOARDING_CALL]: {
      category: OverlayCategory.INACTIVE,
      applies: () => this.shouldScheduleOnboardingCall(),
    },
    [OverlayRule.BIDDER_OFF_WHITELABEL]: {
      category: OverlayCategory.INACTIVE,
      applies: () => this.whitelabelHasBidderOff(),
    },
    [OverlayRule.BIDDER_OFF]: {
      category: OverlayCategory.INACTIVE,
      applies: () => this.bidderIsOff(),
    },
    [OverlayRule.DSP_BIDDER_OFF]: {
      category: OverlayCategory.INACTIVE,
      applies: () => this.dspBidderIsOff(),
    },
    [OverlayRule.DSP_NO_LINKED_ADVERTISER]: {
      category: OverlayCategory.INACTIVE,
      applies: () => this.dspNoLinkedAdvertiser(),
    },
  };

  resolveOverlay(overlays: OverlayComponent[]): Observable<OverlayComponent | null> {
    if (overlays.length === 0) {
      return of(null);
    }
    const prioritizedOverlays = [...overlays].sort((a, b) => this.categoryRank(a) - this.categoryRank(b));
    return combineLatest(prioritizedOverlays.map((overlay) => this.OVERLAY_RULES[overlay.rule].applies())).pipe(
      map((applies) => prioritizedOverlays.find((_, i) => applies[i]) ?? null),
    );
  }

  private categoryRank(overlay: OverlayComponent): number {
    return OverlayService.CATEGORY_PRIORITY.indexOf(this.OVERLAY_RULES[overlay.rule].category);
  }

  private isKdpAccount(): Observable<boolean> {
    return this.selectedAccountMarketplace$.pipe(map((am) => am.accountSubType === AccountSubType.KDP_AUTHOR));
  }

  private whitelabelHasBidderOff(): Observable<boolean> {
    return this.isBidderOn$.pipe(map((isBidderOn) => !isBidderOn && this.boardType !== BoardType.M19));
  }

  private bidderIsOff(): Observable<boolean> {
    return combineLatest([this.isBidderOn$, this.selectedOrg$, this.selectedAccountMarketplace$]).pipe(
      map(([isBidderOn, selectedOrg, accountMarketplace]) => {
        return !isBidderOn && !!selectedOrg?.canActivateBidder(accountMarketplace);
      }),
    );
  }

  private shouldStartFreeTest(): Observable<boolean> {
    return combineLatest([
      this.onboardingService.isPreFreeTest$,
      this.onboardingService.hasScheduledOnboardingCall$,
      this.onboardingService.hasSkippedOnboardingCall$,
    ]).pipe(
      map(
        ([isPreFreeTest, hasScheduledOnboardingCall, hasSkippedOnboardingCall]) =>
          isPreFreeTest && !hasScheduledOnboardingCall && !hasSkippedOnboardingCall,
      ),
    );
  }

  private shouldScheduleOnboardingCall(): Observable<boolean> {
    return this.onboardingService.hasSkippedOnboardingCall$;
  }

  private onboardingCallScheduled(): Observable<boolean> {
    return this.onboardingService.hasScheduledOnboardingCall$;
  }

  private marketplaceNotIncluded(): Observable<boolean> {
    return combineLatest([
      this.isBidderOn$,
      this.selectedOrg$,
      this.selectedAccountMarketplace$,
      this.onboardingService.isPreFreeTest$,
    ]).pipe(
      map(([isBidderOn, selectedOrg, accountMarketplace, isPreFreeTest]) => {
        if (isPreFreeTest) return false;

        const plan = selectedOrg?.getBillingPlan()?.plan;

        return (
          !isBidderOn &&
          !!accountMarketplace.activated &&
          (!selectedOrg?.hasSubscription() || plan == Plan.STARTER || plan == Plan.GROWTH)
        );
      }),
    );
  }

  private manualActivationRequired(): Observable<boolean> {
    return combineLatest([this.isBidderOn$, this.selectedAccountMarketplace$]).pipe(
      map(([isBidderOn, accountMarketplace]) => {
        return !isBidderOn && !accountMarketplace.activated;
      }),
    );
  }

  private hourlyUnsupportedMarketplace(): Observable<boolean> {
    return this.selectedAccountMarketplace$.pipe(map((am) => !Marketplaces[am.marketplace].hourlyDataSupport));
  }

  private hourlyNotInBillingPlan(): Observable<boolean> {
    return this.selectedOrg$.pipe(
      map((selectedOrg) => {
        const plan = selectedOrg?.getBillingPlan();

        return !!plan && !plan.hourlyDataSupport;
      }),
    );
  }

  private dspBidderIsOff(): Observable<boolean> {
    return combineLatest([this.isBidderOn$, this.selectedAccountMarketplace$]).pipe(
      map(([isBidderOn, accountMarketplace]) => {
        const dspAdvertiser = accountMarketplace.dspAdvertiser;

        return (
          isBidderOn &&
          !!dspAdvertiser &&
          dspAdvertiser.bidderOrganizationId != accountMarketplace.resourceOrganizationId
        );
      }),
    );
  }

  private dspNoLinkedAdvertiser(): Observable<boolean> {
    return this.selectedAccountMarketplace$.pipe(map((accountSelection) => !accountSelection.dspAdvertiserId));
  }

  private isVendor(): Observable<boolean> {
    return this.selectedAccountMarketplace$.pipe(map((am) => am.accountType === AccountType.VENDOR));
  }

  private advertisingApiAccessLost(): Observable<boolean> {
    return this.selectedAccountMarketplace$.pipe(map((am) => !am.hasAccessToAdvertising));
  }

  private invalidSellingPartnerApiAccess(): Observable<boolean> {
    return this.selectedAccountMarketplace$.pipe(map((am) => !am.isValidToken));
  }

  private keywordRankingNotSupported(): Observable<boolean> {
    return this.selectedAccountMarketplace$.pipe(map((am) => !keywordRankingAvailableFor(am.marketplace)));
  }
}
